import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, ArrowRight, SkipForward, CheckCircle, Database,
  Warning, FileText,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ====================================================================
 * NOAEL Extraction Console (admin/assessor only)
 *
 * Walks the assessor through Aᴳ Masterlist ingredients that have rich
 * repeated-dose / conclusion text but no quantitative NOAEL yet. The
 * assessor confirms a numeric NOAEL + rationale OR skips ingredients
 * where no quantitative data exists. Persists to db.ingredients so
 * subsequent hazard screens / MoS calculations use the new value.
 * ==================================================================== */

function Stat({ label, value, color = 'text-[#0B1426]' }) {
  return (
    <div className="card-flat p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${color}`}>{value}</p>
    </div>
  );
}

function TextBlock({ label, text }) {
  if (!text) return null;
  return (
    <div className="mb-4" data-testid={`textblock-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF4E6A] mb-1.5">{label}</p>
      <p className="text-[13.5px] leading-relaxed text-[#1B2538] whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export default function NoaelExtractionPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state for the current item
  const [noael, setNoael] = useState('');
  const [route, setRoute] = useState('oral');
  const [rationale, setRationale] = useState('');
  const [uf, setUf] = useState('100');
  const [dermalAbs, setDermalAbs] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/noael-extraction/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      console.warn('[NoaelExtraction] stats load failed:', e?.response?.status);
    }
  }, []);

  const loadQueue = useCallback(async (resetCursor = true) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/noael-extraction/queue?limit=25`, { withCredentials: true });
      setQueue(r.data.items || []);
      if (resetCursor) setCursor(0);
    } catch (e) {
      console.warn('[NoaelExtraction] queue load failed:', e?.response?.status);
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); loadQueue(); }, [loadStats, loadQueue]);

  const current = queue[cursor];

  const resetForm = useCallback(() => {
    setNoael(''); setRoute('oral'); setRationale('');
    setUf('100'); setDermalAbs(''); setSkipReason(''); setError(null);
  }, []);

  useEffect(() => { resetForm(); }, [cursor, resetForm]);

  const advance = () => {
    if (cursor + 1 < queue.length) {
      setCursor(cursor + 1);
    } else {
      // Reached the end of the current batch — fetch next 25.
      loadQueue(true);
      loadStats();
    }
  };

  const confirm = async () => {
    setError(null);
    const numNoael = parseFloat(noael);
    if (!numNoael || numNoael <= 0) { setError('NOAEL must be a positive number (mg/kg bw/day)'); return; }
    if (!rationale.trim()) { setError('Rationale required (study type, species, duration, citation)'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/noael-extraction/${encodeURIComponent(current.inci_name)}/confirm`,
        {
          noael: numNoael,
          noael_route: route,
          noael_rationale: rationale.trim(),
          uncertainty_factor: parseFloat(uf) || 100,
          dermal_absorption: dermalAbs ? parseFloat(dermalAbs) : null,
        },
        { withCredentials: true },
      );
      advance();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/noael-extraction/${encodeURIComponent(current.inci_name)}/skip`,
        { reason: skipReason.trim() || null },
        { withCredentials: true },
      );
      advance();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Skip failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4E6A] mb-2">
              Aᴳ Assessor Console
            </p>
            <h1 className="text-3xl lg:text-4xl font-serif text-[#0B1426] leading-tight">
              NOAEL Extraction
            </h1>
            <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">
              Walk the Aᴳ Masterlist. Confirm a numeric NOAEL where the source data supports it, or
              skip when only qualitative information is available. Every confirmed value is signed,
              timestamped, and feeds into every subsequent MoS calculation.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/assessor')}
            data-testid="back-to-assessor"
          >
            <ArrowLeft size={14} /> Assessor Console
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="noael-stats">
            <Stat label="Ingredients" value={stats.total_ingredients.toLocaleString()} />
            <Stat label="With NOAEL" value={stats.with_noael.toLocaleString()} color="text-[#03543F]" />
            <Stat label="Coverage" value={`${stats.coverage_pct}%`} color="text-[#FF4E6A]" />
            <Stat label="Pending" value={stats.pending_extraction.toLocaleString()} color="text-[#723B13]" />
            <Stat label="Skipped" value={stats.skipped.toLocaleString()} color="text-[#4B5563]" />
          </div>
        )}

        {loading ? (
          <div className="card-flat p-12 text-center text-[#4B5563]" data-testid="noael-loading">
            Loading queue…
          </div>
        ) : !current ? (
          <div className="card-flat p-12 text-center" data-testid="noael-empty">
            <CheckCircle size={48} weight="duotone" className="mx-auto mb-4 text-[#03543F]" />
            <p className="font-serif text-xl text-[#0B1426]">Queue empty.</p>
            <p className="text-sm text-[#4B5563] mt-2">
              No remaining ingredients with extractable source text. Check back after the next
              Masterlist refresh.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Source panel */}
            <section className="lg:col-span-7 card-flat p-7" data-testid="noael-source">
              <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#4B5563] mb-1">
                    Item {cursor + 1} of {queue.length} in this batch
                  </p>
                  <h2 className="font-serif text-2xl text-[#0B1426] leading-tight" data-testid="current-inci">
                    {current.inci_name}
                  </h2>
                  <p className="text-[12px] font-mono text-[#4B5563] mt-1">
                    {current.cas_number ? `CAS ${current.cas_number}` : 'No CAS'} ·
                    {' '}{current.function_class || '—'}
                    {current.molecular_weight ? ` · MW ${current.molecular_weight}` : ''}
                  </p>
                </div>
                <Database size={20} weight="duotone" className="text-[#FF4E6A] flex-shrink-0" />
              </div>

              <TextBlock label="Repeated Dose" text={current.repeated_dose_text} />
              <TextBlock label="Conclusions" text={current.conclusions} />
              <TextBlock label="Carcinogenicity" text={current.carcinogenicity_text} />
              <TextBlock label="Reprotox" text={current.reprotox_text} />
              <TextBlock label="Acute Oral" text={current.acute_oral_text} />
              <TextBlock label="Dermal Absorption" text={current.dermal_absorption_text} />
              <TextBlock label="Notes" text={current.notes} />

              {(current.source || current.source_links) && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-start gap-2">
                  <FileText size={14} className="text-[#4B5563] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[11px] text-[#4B5563]">{current.source}</p>
                    {current.source_links && (
                      <p className="text-[11px] text-[#4B5563] mt-1 break-all">{current.source_links}</p>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Form panel */}
            <aside className="lg:col-span-5 card-flat p-7 self-start sticky top-6" data-testid="noael-form">
              <h3 className="font-serif text-lg text-[#0B1426] mb-1">Confirm NOAEL</h3>
              <p className="text-[12px] text-[#4B5563] mb-5">
                Capture the value, route, and the study that supports it.
              </p>

              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                NOAEL (mg/kg bw/day)
              </label>
              <Input
                type="number" step="any" inputMode="decimal" value={noael}
                onChange={(e) => setNoael(e.target.value)}
                placeholder="e.g. 150"
                className="mb-4" data-testid="noael-input"
              />

              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Route
              </label>
              <select
                value={route} onChange={(e) => setRoute(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4 bg-white"
                data-testid="route-select"
              >
                <option value="oral">Oral</option>
                <option value="dermal">Dermal</option>
                <option value="inhalation">Inhalation</option>
              </select>

              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Rationale & citation
              </label>
              <Textarea
                value={rationale} onChange={(e) => setRationale(e.target.value)}
                placeholder="e.g. CIR 90-day rat OECD 408, NOAEL 150 mg/kg bw/day. CIR final report 2019."
                rows={4} className="mb-4" data-testid="rationale-input"
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                    UF
                  </label>
                  <Input
                    type="number" step="any" value={uf}
                    onChange={(e) => setUf(e.target.value)}
                    data-testid="uf-input"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                    Dermal abs. (0–1)
                  </label>
                  <Input
                    type="number" step="any" min="0" max="1" value={dermalAbs}
                    onChange={(e) => setDermalAbs(e.target.value)}
                    placeholder="optional"
                    data-testid="dermal-abs-input"
                  />
                </div>
              </div>

              {error && (
                <p className="mt-4 text-[12px] text-[#9B1C1C] flex items-center gap-1.5" data-testid="noael-error">
                  <Warning size={13} /> {error}
                </p>
              )}

              <div className="flex gap-2 mt-6">
                <Button
                  onClick={confirm} disabled={saving}
                  className="flex-1 bg-[#0B1426] hover:bg-[#1B2538] text-white"
                  data-testid="confirm-btn"
                >
                  <CheckCircle size={14} /> Confirm & next
                </Button>
              </div>

              <div className="mt-5 pt-5 border-t border-gray-100">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                  Skip reason (optional)
                </label>
                <Input
                  value={skipReason} onChange={(e) => setSkipReason(e.target.value)}
                  placeholder="e.g. Only acute LD50; TTC default applies"
                  className="mb-3 text-[12px]" data-testid="skip-reason-input"
                />
                <Button
                  variant="outline" onClick={skip} disabled={saving}
                  className="w-full text-[#4B5563]"
                  data-testid="skip-btn"
                >
                  <SkipForward size={13} /> Skip — no quant. data available
                </Button>
              </div>

              <button
                type="button"
                onClick={advance}
                className="w-full mt-3 text-[11px] text-[#4B5563] hover:text-[#FF4E6A] flex items-center justify-center gap-1"
                data-testid="next-without-action"
              >
                Defer to next batch <ArrowRight size={11} />
              </button>
            </aside>
          </div>
        )}

        <p className="text-[11px] text-[#4B5563] text-center mt-8">
          Aᴳ Assessor Console &middot; <Link to="/assessor" className="hover:text-[#FF4E6A]">Back to overview</Link>
        </p>
      </main>
    </div>
  );
}
